import {ResponseValidator} from './../utils/response-validator';
import {Observable} from 'rxjs';
import {localConsole} from './../utils/local-console';

/**
 * A class that help saving API responses in session or local storage
 * using the request payload or provided lookup key
 *
 * To save a request
 * ```
 * this.saveInStorageIfValid(storage: WindowSessionStorage | WindowLocalStorage, requestObj, response);
 *
 * // or
 * this.saveInStorageIfValid(storage: WindowSessionStorage | WindowLocalStorage, requestObj, response, 'time In Milliseconds');
 *
 * ```
 *
 * To retrieve a request
 *
 * (): Observable<any> => {
 *    return (this.pullFromStorage(storage: WindowSessionStorage | WindowLocalStorage, requestObj)
 *        || this.httpClient.post('/api', requestObj)
 * }
 */
export class CanSaveInStorage {
  /**
   * Check if expire date is not expired,
   * will delete key if expired
   */
  checkValidityInStorage(storage: WindowSessionStorage | WindowLocalStorage | any, key, data: any): any | null {
    if (data.expire) {
      if (data.expire > new Date().getTime()) {
        return data;
      } else {
        storage.removeItem(key);
      }
    }
    return null;
  }
  /**
   * Save response with desired storage with request object as key
   * Exclude failure calls, or status code other than 0000
   * 24 hours by default
   * param req | object
   * param res | object
   */
  saveInStorageIfValid(storage: WindowSessionStorage | WindowLocalStorage | any,
                       req: any, response,
                       ttl_mlsc?: number,
                       statusCodeOnlyCheck?: boolean) {
    if (new ResponseValidator(response, statusCodeOnlyCheck).status) {
      const key = typeof req === 'string' ? req : JSON.stringify(req);
      storage.setItem(key, JSON.stringify({
        expire: new Date().getTime() + (ttl_mlsc || (1000 * 60 * 60 * 24)), // 24 hours
        request: key,
        response: response
      }));
    }
    return response;
  }

  /**
   * Pull response object from storage
   */
  pullFromStorage(storage: WindowSessionStorage | WindowLocalStorage | any, req) {
    const key = typeof req === 'string' ? req : JSON.stringify(req);
    let saved: any = storage.getItem(key);
    if (!saved) {
      return null;
    }
    try {
      saved = JSON.parse(saved);
    } catch (e) {
      localConsole.log(e);
      return null;
    }
    if (!this.checkValidityInStorage(storage, key, saved)) {
      return null;
    }
    const c = new Observable(observer => {
      observer.next(saved.response);
      observer.complete();
    });
    return c;
  }
}
